/* sb-dc-imgtools.js - image tools for a selected image layer.

   1. "Replace this image" - swap the picture inside a template's photo slot
      without disturbing the slot. The layer keeps its exact position, size and
      rotation; the new picture is centre-cropped to the slot's aspect ratio on
      a canvas first, so nothing stretches.

      2026-09-03 (design team request): the slot's SHAPE is kept too. Template
      photo slots are circles, triangles, arches - the placeholder picture
      carries that shape in its alpha channel. Dropping a rectangular photo in
      used to square the slot off and break the layout. Now the placeholder's
      alpha is read first and used as a mask (destination-in), so the customer's
      photo arrives in the same shape, with the same soft edge. A placeholder
      that is already a plain rectangle has no mask to keep, so those behave
      exactly as before. See maskFromPlaceholder / shouldMask.

   2. "Remove background" - two modes. "AI cut-out" runs an ISNet segmentation
      model in the browser (@imgly/background-removal) and handles photos,
      people and products on any background; the model downloads once (~88MB)
      and each cut takes 10-25s of CPU. "Flat colour" is the instant border
      flood fill with a tolerance slider, right for logos on a plain ground.
      Both show a preview before anything is applied.

   Both run entirely in the browser. Nothing is uploaded until the design is
   added to the cart, exactly as before.

   Where the buttons live: they are injected into the Image panel's "Selected
   image" section AND into a small bar that follows the selected picture on the
   artboard (2026-09-03 design team request - the tools were three clicks deep
   inside a panel). Both are re-injected on a timer because the panel is React's
   and repaints whenever it likes.

   2026-09-03 (second pass, user feedback on the preview): the bar was parked at
   the top of the artboard, far from the picture, and its labels were invisible.
   Two causes - role="toolbar" made the editor's own CSS paint it dark, and the
   bar was positioned against the artboard container rather than the selection.
   Now it is styled with !important, carries role="group", and sits directly
   above the selected picture (see selectionRect / placeBar).

   2026-09-03 (third pass): the printed-colour disclaimer that briefly sat under
   the artboard was taken out again at the user's request. It stays on the
   Design Templates page only - sections/sb-dc-colornote.liquid. */
(function () {
  'use strict';

  var MAX_SIDE = 3000;          // cap on any canvas we produce
  var MAX_PIXELS = 6e6;         // cap before we downscale for the fill
  var TAG = 'sbdc-imgtools';
  var BAR_ID = TAG + '-bar';

  /* ---------- small helpers ---------- */

  function api() { return window.__dcApi || null; }

  function selectedImage() {
    var a = api();
    if (!a || !a.selectedId) return null;
    var layers = (a.front && a.front.layers) || [];
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].id === a.selectedId && layers[i].type === 'image') return layers[i];
    }
    return null;
  }

  function loadImage(src) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error('image load failed')); };
      im.src = src;
    });
  }

  function readFile(file) {
    return new Promise(function (res, rej) {
      var fr = new FileReader();
      fr.onload = function () { res(fr.result); };
      fr.onerror = function () { rej(new Error('read failed')); };
      fr.readAsDataURL(file);
    });
  }

  function toast(msg, bad) {
    var el = document.createElement('div');
    el.textContent = msg;
    el.style.cssText = 'position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:2147483646;' +
      'background:' + (bad ? '#b3261e' : '#00697f') + ';color:#fff;padding:12px 18px;border-radius:10px;' +
      'font:600 14px/1.4 Archivo,system-ui,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,.25);max-width:min(520px,92vw)';
    document.body.appendChild(el);
    setTimeout(function () { el.remove(); }, 4200);
  }

  /* ---------- 1. replace the picture in the slot ---------- */

  /* Centre-crop src to the slot's aspect ratio. Cropping rather than stretching
     is the only honest way to keep both the slot and the picture's proportions. */
  function fitToSlot(im, slotW, slotH) {
    var target = slotW / slotH;
    var sw = im.naturalWidth, sh = im.naturalHeight;
    var cw = sw, ch = Math.round(sw / target);
    if (ch > sh) { ch = sh; cw = Math.round(sh * target); }
    var sx = Math.round((sw - cw) / 2), sy = Math.round((sh - ch) / 2);

    var outW = cw, outH = ch;
    var scale = Math.min(1, MAX_SIDE / Math.max(outW, outH));
    outW = Math.max(1, Math.round(outW * scale));
    outH = Math.max(1, Math.round(outH * scale));

    var c = document.createElement('canvas');
    c.width = outW; c.height = outH;
    c.getContext('2d').drawImage(im, sx, sy, cw, ch, 0, 0, outW, outH);
    return c;
  }

  /* How much of the placeholder is solid. 1 = a plain rectangle (nothing to
     keep), 0 = fully transparent (something is wrong, keep out of it). */
  function opaqueRatio(data) {
    var n = data.length / 4, op = 0;
    for (var i = 3; i < data.length; i += 4) if (data[i] >= 250) op++;
    return n ? op / n : 1;
  }
  /* A shape worth keeping: clearly not a full rectangle, and not so empty that
     the customer's photo would all but disappear. Exported for the test
     harness as well as for anyone debugging a slot that did not mask. */
  function shouldMask(ratio) { return ratio < 0.985 && ratio > 0.05; }

  /* Draw the placeholder at the size of the new picture and return it only if
     its alpha actually describes a shape. Anything at all goes wrong here -
     a cross-origin image that will not give up its pixels, most likely - and
     we return null, which just means the old behaviour. */
  function maskFromPlaceholder(im, w, h) {
    try {
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(im, 0, 0, w, h);
      var d = ctx.getImageData(0, 0, w, h).data;
      var r = opaqueRatio(d);
      window.__dcMaskLast = { ratio: Math.round(r * 1000) / 1000, used: shouldMask(r) };
      return shouldMask(r) ? c : null;
    } catch (e) {
      window.__dcMaskLast = { error: String(e && e.message || e) };
      return null;
    }
  }

  /* Cut the new picture to the placeholder's silhouette. destination-in keeps
     the source pixels only where the mask has alpha, and it keeps partial
     alpha, so an anti-aliased circle edge stays anti-aliased. */
  function applyMask(photo, mask) {
    var ctx = photo.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0, photo.width, photo.height);
    ctx.restore();
    return photo;
  }

  function replaceFlow(layer, file) {
    var a = api();
    if (!a) return;
    var masked = false;
    readFile(file)
      .then(loadImage)
      .then(function (im) {
        var w = layer.widthIn, h = layer.heightIn;
        var isSvg = /svg/i.test(file.type || '');
        if (isSvg) {
          /* Vectors have no pixels to crop and scale cleanly anyway - drop it in
             as is and let the layer box hold it. A vector carries its own shape,
             so there is nothing to mask either. */
          return readFile(file).then(function (src) {
            a.updateLayer(layer.id, {
              src: src, naturalWidth: im.naturalWidth || 1000,
              naturalHeight: im.naturalHeight || 1000,
              isVector: true, fileName: file.name
            });
          });
        }
        var c = fitToSlot(im, w, h);
        /* Keep the slot's shape: read it off the picture being replaced. */
        return loadImage(layer.src).then(function (old) {
          var mask = maskFromPlaceholder(old, c.width, c.height);
          if (mask) { applyMask(c, mask); masked = true; }
        }).catch(function () { /* no mask available - plain replace */ })
          .then(function () {
            a.updateLayer(layer.id, {
              src: c.toDataURL('image/png'),
              naturalWidth: c.width, naturalHeight: c.height,
              isVector: false, fileName: file.name
            });
          });
      })
      .then(function () {
        toast(masked
          ? 'Picture replaced, cut to the template\u2019s shape.'
          : 'Picture replaced. The slot kept its position and size.');
      })
      .catch(function () { toast('That file could not be read. Try a PNG or JPG.', true); });
  }

  /* ---------- 2. background removal ---------- */

  /* Flood fill from the border. Every border pixel seeds a region and the region
     grows while neighbours stay within `tol` of that seed's colour, so a picture
     with a white top and a grey bottom loses both. */
  function cutOut(img, tol) {
    var w = img.width, h = img.height;
    var ctx = img.getContext('2d');
    var data = ctx.getImageData(0, 0, w, h);
    var px = data.data;
    var seen = new Uint8Array(w * h);
    var cut = new Uint8Array(w * h);
    var stack = new Int32Array(w * h);
    var t2 = tol * tol * 3;

    function seed(start) {
      var sr = px[start * 4], sg = px[start * 4 + 1], sb = px[start * 4 + 2];
      var top = 0;
      stack[top++] = start;
      seen[start] = 1;
      while (top > 0) {
        var p = stack[--top];
        var o = p * 4;
        var dr = px[o] - sr, dg = px[o + 1] - sg, db = px[o + 2] - sb;
        if (dr * dr + dg * dg + db * db > t2) continue;
        cut[p] = 1;
        var x = p % w, y = (p / w) | 0;
        if (x > 0 && !seen[p - 1]) { seen[p - 1] = 1; stack[top++] = p - 1; }
        if (x < w - 1 && !seen[p + 1]) { seen[p + 1] = 1; stack[top++] = p + 1; }
        if (y > 0 && !seen[p - w]) { seen[p - w] = 1; stack[top++] = p - w; }
        if (y < h - 1 && !seen[p + w]) { seen[p + w] = 1; stack[top++] = p + w; }
      }
    }

    var i;
    for (i = 0; i < w; i++) { if (!cut[i]) seed(i); if (!cut[(h - 1) * w + i]) seed((h - 1) * w + i); }
    for (i = 0; i < h; i++) { if (!cut[i * w]) seed(i * w); if (!cut[i * w + w - 1]) seed(i * w + w - 1); }

    /* One soft pass so the cut edge is not a staircase. */
    for (i = 0; i < w * h; i++) {
      if (cut[i]) { px[i * 4 + 3] = 0; continue; }
      var x2 = i % w, y2 = (i / w) | 0, edge = false;
      if (x2 > 0 && cut[i - 1]) edge = true;
      else if (x2 < w - 1 && cut[i + 1]) edge = true;
      else if (y2 > 0 && cut[i - w]) edge = true;
      else if (y2 < h - 1 && cut[i + w]) edge = true;
      if (edge) px[i * 4 + 3] = Math.round(px[i * 4 + 3] * 0.55);
    }
    ctx.putImageData(data, 0, 0);
    return img;
  }

  function sourceCanvas(im) {
    var w = im.naturalWidth, h = im.naturalHeight;
    var scale = Math.min(1, Math.sqrt(MAX_PIXELS / (w * h)), MAX_SIDE / Math.max(w, h));
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * scale));
    c.height = Math.max(1, Math.round(h * scale));
    c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
    return c;
  }

  /* AI segmentation (ISNet via @imgly/background-removal) runs entirely in the
     browser: the model (~88MB) is downloaded once and cached, then every cut
     takes 10-25 seconds of CPU. Quality is the same class as the hosted
     services; the wait is the price of not sending customer photos anywhere.
     The flat-colour flood fill stays as the quick option for logos. */
  var AI_URL = 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.6.0/+esm';
  var aiMod = null;
  /* Shopify's asset pipeline runs theme JS through esbuild, which rewrites a
     literal import() into a CommonJS require() that does not exist in the
     browser. Building the import in a string keeps the bundler's hands off it. */
  var dynImport = new Function('u', 'return import(u)');
  function aiModule() {
    if (aiMod) return aiMod;
    aiMod = dynImport(AI_URL).catch(function (e) { aiMod = null; throw e; });
    return aiMod;
  }

  function canvasToBlob(c) {
    return new Promise(function (res) { c.toBlob(res, 'image/png'); });
  }

  function bgModal(layer) {
    var a = api();
    if (!a) return;
    if (layer.isVector) { toast('This is a vector graphic - it has no background to remove.', true); return; }

    var back = document.createElement('div');
    back.style.cssText = 'position:fixed;inset:0;z-index:2147483645;background:rgba(15,23,32,.55);' +
      'display:flex;align-items:center;justify-content:center;padding:20px';
    var box = document.createElement('div');
    box.style.cssText = 'background:#fff;border-radius:14px;max-width:min(720px,94vw);width:100%;' +
      'padding:20px;font:400 14px/1.5 Archivo,system-ui,sans-serif;box-shadow:0 18px 60px rgba(0,0,0,.3)';
    var tabCss = 'flex:1;padding:9px 12px;border-radius:8px;border:1px solid #c9d1d9;background:#fff;font-weight:600;cursor:pointer';
    box.innerHTML =
      '<h3 style="margin:0 0 4px;font:700 17px/1.3 Archivo,system-ui,sans-serif">Remove background</h3>' +
      '<div style="display:flex;gap:8px;margin:10px 0 12px">' +
      '<button id="' + TAG + '-mode-ai" style="' + tabCss + '">AI cut-out (photos, people, products)</button>' +
      '<button id="' + TAG + '-mode-flat" style="' + tabCss + '">Flat colour (logos on plain background)</button>' +
      '</div>' +
      '<p id="' + TAG + '-hint" style="margin:0 0 12px;color:#5b6672"></p>' +
      '<div id="' + TAG + '-stage" style="background-image:linear-gradient(45deg,#e6e9ec 25%,transparent 25%),' +
      'linear-gradient(-45deg,#e6e9ec 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e6e9ec 75%),' +
      'linear-gradient(-45deg,transparent 75%,#e6e9ec 75%);background-size:18px 18px;' +
      'background-position:0 0,0 9px,9px -9px,-9px 0;border:1px solid #d7dde3;border-radius:10px;' +
      'display:flex;align-items:center;justify-content:center;min-height:220px;overflow:hidden;position:relative"></div>' +
      '<div id="' + TAG + '-prog" style="height:6px;background:#e6e9ec;border-radius:3px;margin-top:10px;overflow:hidden;display:none">' +
      '<div id="' + TAG + '-bar" style="height:100%;width:0;background:#00697f;transition:width .2s"></div></div>' +
      '<label id="' + TAG + '-tolrow" style="display:none;align-items:center;gap:12px;margin:16px 0 4px">' +
      '<span style="font-weight:600">Tolerance</span>' +
      '<input id="' + TAG + '-tol" type="range" min="4" max="120" value="32" style="flex:1">' +
      '<span id="' + TAG + '-tolv" style="width:34px;text-align:right;font-variant-numeric:tabular-nums">32</span>' +
      '</label>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:14px">' +
      '<button id="' + TAG + '-cancel" style="padding:10px 16px;border-radius:8px;border:1px solid #c9d1d9;' +
      'background:#fff;font-weight:600;cursor:pointer">Cancel</button>' +
      '<button id="' + TAG + '-apply" style="padding:10px 18px;border-radius:8px;border:0;background:#00697f;' +
      'color:#fff;font-weight:700;cursor:pointer" disabled>Apply</button>' +
      '</div>';
    back.appendChild(box);
    document.body.appendChild(back);

    var $ = function (id) { return box.querySelector('#' + TAG + '-' + id); };
    var stage = $('stage'), hint = $('hint'), prog = $('prog'), bar = $('bar');
    var tolRow = $('tolrow'), tol = $('tol'), tolv = $('tolv');
    var applyBtn = $('apply'), modeAi = $('mode-ai'), modeFlat = $('mode-flat');
    var base = null, result = null, mode = 'ai', aiJob = 0, aiResult = null;
    var busy = false, pending = false;

    function close() { aiJob++; back.remove(); }
    back.addEventListener('click', function (e) { if (e.target === back) close(); });
    $('cancel').addEventListener('click', close);

    function show(canvas) {
      stage.innerHTML = '';
      var pv = new Image();
      pv.src = canvas.toDataURL('image/png');
      pv.style.cssText = 'max-width:100%;max-height:46vh;display:block';
      stage.appendChild(pv);
    }
    function status(msg) {
      stage.innerHTML = '<div style="padding:24px;text-align:center;color:#33404d;font-weight:600">' + msg + '</div>';
    }
    function setMode(m) {
      mode = m;
      modeAi.style.background = m === 'ai' ? '#e6f4f7' : '#fff';
      modeAi.style.borderColor = m === 'ai' ? '#00697f' : '#c9d1d9';
      modeFlat.style.background = m === 'flat' ? '#e6f4f7' : '#fff';
      modeFlat.style.borderColor = m === 'flat' ? '#00697f' : '#c9d1d9';
      tolRow.style.display = m === 'flat' ? 'flex' : 'none';
      applyBtn.disabled = true;
      result = null;
      if (m === 'flat') {
        hint.textContent = 'Drag the slider until only the subject is left. Best for logos on one flat colour.';
        prog.style.display = 'none';
        renderFlat();
      } else {
        hint.textContent = 'Finds the subject and removes everything else. First use downloads the model (~88MB); each cut then takes about 10-25 seconds.';
        runAi();
      }
    }
    modeAi.addEventListener('click', function () { setMode('ai'); });
    modeFlat.addEventListener('click', function () { setMode('flat'); });

    function renderFlat() {
      if (!base) return;
      if (busy) { pending = true; return; }
      busy = true;
      setTimeout(function () {
        var c = document.createElement('canvas');
        c.width = base.width; c.height = base.height;
        c.getContext('2d').drawImage(base, 0, 0);
        result = cutOut(c, +tol.value);
        show(result);
        applyBtn.disabled = false;
        busy = false;
        if (pending) { pending = false; renderFlat(); }
      }, 0);
    }
    tol.addEventListener('input', function () { tolv.textContent = tol.value; if (mode === 'flat') renderFlat(); });

    function runAi() {
      if (!base) return;
      if (aiResult) { result = aiResult; show(result); applyBtn.disabled = false; return; }
      var job = ++aiJob;
      status('Loading the cut-out model...');
      prog.style.display = 'block'; bar.style.width = '0%';
      aiModule().then(function (mod) {
        if (job !== aiJob) return;
        return canvasToBlob(base).then(function (blob) {
          status('Finding the subject... this takes about 10-25 seconds.');
          return mod.removeBackground(blob, {
            progress: function (key, cur, tot) {
              if (job !== aiJob || !tot) return;
              if (/fetch/.test(key)) bar.style.width = Math.round(cur / tot * 100) + '%';
            }
          });
        });
      }).then(function (out) {
        if (job !== aiJob || !out) return;
        var url = URL.createObjectURL(out);
        return loadImage(url).then(function (im) {
          URL.revokeObjectURL(url);
          var c = document.createElement('canvas');
          c.width = im.naturalWidth; c.height = im.naturalHeight;
          c.getContext('2d').drawImage(im, 0, 0);
          aiResult = c; result = c;
          bar.style.width = '100%';
          show(c);
          applyBtn.disabled = false;
        });
      }).catch(function () {
        if (job !== aiJob) return;
        status('The AI cut-out could not run in this browser. Try "Flat colour", or a different browser.');
        prog.style.display = 'none';
      });
    }

    applyBtn.addEventListener('click', function () {
      if (!result) return;
      try {
        api().updateLayer(layer.id, {
          src: result.toDataURL('image/png'),
          naturalWidth: result.width, naturalHeight: result.height,
          isVector: false
        });
        close();
        toast('Background removed.');
      } catch (e) { toast('That did not apply. Please try again.', true); }
    });

    status('Opening the picture...');
    loadImage(layer.src).then(function (im) {
      base = sourceCanvas(im);
      setMode('ai');
    }).catch(function () {
      status('This picture could not be opened.');
    });
  }

  /* ---------- shared actions ---------- */

  function pickFile(cb) {
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';
    input.style.display = 'none';
    document.body.appendChild(input);
    input.addEventListener('change', function () {
      var f = input.files && input.files[0];
      input.remove();
      if (f) cb(f);
    });
    input.click();
  }
  function doReplace() {
    var l = selectedImage();
    if (!l) return;
    pickFile(function (f) { replaceFlow(selectedImage() || l, f); });
  }
  function doRemoveBg() {
    var l = selectedImage();
    if (l) bgModal(l);
  }

  /* ---------- panel wiring (Image panel, "Selected image") ---------- */

  function makeButton(label, onClick) {
    var b = document.createElement('button');
    b.className = 'fp-cta is-ghost ' + TAG + '-btn';
    b.type = 'button';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  function injectPanel() {
    var titles = document.querySelectorAll('h3.fp-title');
    var host = null;
    for (var i = 0; i < titles.length; i++) {
      if (/selected image/i.test(titles[i].textContent || '')) { host = titles[i].parentElement; break; }
    }
    /* 2026-09-22: no selected picture here (deselected, or another panel) -
       take the buttons away. React leaves nodes it did not make, so they
       used to stay behind at the top of every panel, doing nothing. */
    if (!host || !selectedImage()) {
      var old = document.querySelectorAll('.' + TAG + '-btn');
      for (var k = 0; k < old.length; k++) old[k].remove();
      return;
    }
    if (host.querySelector('.' + TAG + '-btn')) return;      // already there for this render
    host.appendChild(makeButton('Replace this image', doReplace));
    host.appendChild(makeButton('Remove background', doRemoveBg));
  }

  /* ---------- the bar that follows the selected picture ----------

     Fixed rather than inserted into the layout on purpose: the editor measures
     .canvas-viewport and resizes the Konva stage to it, so putting a new
     element inside that column would start a fight over the artboard's height.
     This one only sits over it. */

  var ICON_REPLACE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>' +
    '<path d="M21 15l-5-5L5 21"/></svg>';
  var ICON_CUT =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.12 15.88"/>' +
    '<path d="M14.47 14.48L20 20"/><path d="M8.12 8.12L12 12"/></svg>';

  function barCss() {
    if (document.getElementById(TAG + '-css')) return;
    var st = document.createElement('style');
    st.id = TAG + '-css';
    /* !important throughout: the editor's own stylesheets paint anything that
       looks like a toolbar dark, which is what swallowed the button labels the
       first time round (white bar, dark text, dark background underneath). */
    st.textContent =
      '#' + BAR_ID + '{position:fixed !important;z-index:2147483000 !important;display:none;' +
      'gap:6px;align-items:center;background:#fff !important;border:1px solid #cbd5dc !important;' +
      'border-radius:10px !important;padding:5px 7px !important;' +
      'box-shadow:0 6px 20px rgba(12,32,44,.22) !important;' +
      'font:600 13px/1 Archivo,system-ui,sans-serif !important;color:#12232b !important}' +
      '#' + BAR_ID + '.is-on{display:flex !important}' +
      '#' + BAR_ID + ' button{display:inline-flex !important;align-items:center;gap:7px;' +
      'border:0 !important;background:#fff !important;color:#12232b !important;' +
      'font:600 13px/1 Archivo,system-ui,sans-serif !important;padding:7px 10px !important;' +
      'border-radius:8px !important;cursor:pointer;white-space:nowrap;text-transform:none !important;' +
      'letter-spacing:0 !important;box-shadow:none !important;min-height:0 !important}' +
      '#' + BAR_ID + ' button span{color:#12232b !important}' +
      '#' + BAR_ID + ' button:hover,#' + BAR_ID + ' button:hover span{background:#e6f4f7 !important;color:#00697f !important}' +
      '#' + BAR_ID + ' svg{flex:none}' +
      '#' + BAR_ID + ' .' + TAG + '-sep{width:1px;height:20px;background:#e2e9ec !important}' +
      '#' + BAR_ID + ' .' + TAG + '-lbl{color:#7c9098 !important;background:#fff !important;font-weight:600;' +
      'max-width:170px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-left:4px}';
    document.head.appendChild(st);
  }

  function bar() {
    var el = document.getElementById(BAR_ID);
    if (el) return el;
    barCss();
    el = document.createElement('div');
    el.id = BAR_ID;
    /* Not role="toolbar": the editor's CSS paints that role dark. */
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', 'Image tools');
    var b1 = document.createElement('button');
    b1.type = 'button'; b1.innerHTML = ICON_REPLACE + '<span>Replace image</span>';
    b1.addEventListener('click', doReplace);
    var b2 = document.createElement('button');
    b2.type = 'button'; b2.innerHTML = ICON_CUT + '<span>Remove background</span>';
    b2.addEventListener('click', doRemoveBg);
    var sep = document.createElement('span'); sep.className = TAG + '-sep';
    var lbl = document.createElement('span'); lbl.className = TAG + '-lbl';
    el.appendChild(b1); el.appendChild(b2); el.appendChild(sep); el.appendChild(lbl);
    document.body.appendChild(el);
    return el;
  }

  /* Where the selected picture actually is on screen.

     The artboard is a Konva stage, so the selection's box comes from the
     Transformer Konva draws around it - the same dashed box the customer sees.
     getClientRect() gives it in stage pixels; the stage container's own
     bounding rect turns those into window coordinates. If anything is missing
     we fall back to the layer's own node, and failing that to the top of the
     artboard, which is where the bar used to sit permanently. */
  function stage() {
    var K = window.Konva;
    return (K && K.stages && K.stages.length) ? K.stages[0] : null;
  }
  function selectionRect(layer) {
    var st = stage();
    if (!st) return null;
    var host = st.container();
    if (!host) return null;
    var base = host.getBoundingClientRect();
    var box = null;
    try {
      var trs = st.find('Transformer');
      for (var i = 0; i < trs.length; i++) {
        if (trs[i].isVisible && trs[i].isVisible() && trs[i].nodes && trs[i].nodes().length) {
          var r = trs[i].getClientRect();
          if (r && r.width > 1 && r.height > 1) { box = r; break; }
        }
      }
    } catch (e) {}
    if (!box) {
      try {
        var node = st.findOne('#' + layer.id);
        if (node) {
          var r2 = node.getClientRect();
          if (r2 && r2.width > 1) box = r2;
        }
      } catch (e) {}
    }
    if (!box) return null;
    return {
      left: base.left + box.x, top: base.top + box.y,
      right: base.left + box.x + box.width, bottom: base.top + box.y + box.height
    };
  }

  function placeBar() {
    var el = bar();
    var layer = selectedImage();
    var box = document.querySelector('.canvas-viewport');
    if (!layer || !box) { el.classList.remove('is-on'); return; }
    var view = box.getBoundingClientRect();
    if (view.width < 60 || view.height < 60) { el.classList.remove('is-on'); return; }

    el.querySelector('.' + TAG + '-lbl').textContent = layer.name || layer.fileName || 'Image';
    el.classList.add('is-on');
    var w = el.offsetWidth || 320, h = el.offsetHeight || 38;

    var sel = selectionRect(layer);
    var left, top;
    if (sel) {
      /* Right above the picture, and below it when the picture is near the top
         of the artboard. */
      left = Math.round(sel.left + (sel.right - sel.left - w) / 2);
      top = Math.round(sel.top - h - 10);
      if (top < view.top + 6) top = Math.round(sel.bottom + 10);
      if (top + h > view.bottom - 6) top = Math.round(Math.max(view.top + 6, sel.top - h - 10));
    } else {
      left = Math.round(view.left + (view.width - w) / 2);
      top = Math.round(view.top + 8);
    }
    /* Never leave the artboard area or the window. */
    top = Math.max(view.top + 6, Math.min(top, view.bottom - h - 6));
    left = Math.max(Math.max(8, view.left + 6), Math.min(left, Math.min(window.innerWidth, view.right) - w - 6));
    el.style.left = left + 'px';
    el.style.top = top + 'px';
  }

  var timer = setInterval(function () {
    try { injectPanel(); } catch (e) {}
    try { placeBar(); } catch (e) {}
  }, 200);
  window.addEventListener('resize', function () { try { placeBar(); } catch (e) {} });
  window.addEventListener('scroll', function () { try { placeBar(); } catch (e) {} }, true);
  window.addEventListener('pagehide', function () { clearInterval(timer); });

  /* Handles for debugging from the console, and for the test harness. */
  window.__dcImgTools = {
    replace: doReplace, removeBg: doRemoveBg, rect: selectionRect,
    opaqueRatio: opaqueRatio, shouldMask: shouldMask,
    applyMask: applyMask, place: placeBar
  };
})();
